import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetHeader, SheetTitle, SheetBody, SheetActions } from './ui/Sheet';
import { Button } from './ui/Button';
import { CheckCircle } from 'lucide-react';
import './TableBookingSheet.css';

interface RestaurantConfig {
  id: string;
  tableCount: number;
  todayHours?: { open: string; close: string } | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  restaurant: RestaurantConfig;
  customerName?: string | null;
  customerPhone?: string | null;
  /** Optional sign-in CTA — shown as a soft suggestion, never blocks the booking. */
  onSignInRequired?: () => void;
}

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const PHONE_REGEX = /^\+?[1-9]\d{7,14}$/;

function timeToMin(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

const GUEST_OPTIONS = [1, 2, 3, 4, 5, 6];

/**
 * Reservation-only sheet (no food order).
 * Always collects name + phone — restaurants need a way to confirm/cancel the booking
 * regardless of customer-auth status. Login is offered as an optional perk.
 *
 * MVP: persists nothing on backend yet — shows confirmation locally.
 * TODO: wire to POST /api/bookings when schema lands.
 */
export function TableBookingSheet({
  open,
  onClose,
  restaurant,
  customerName,
  customerPhone,
  onSignInRequired,
}: Props) {
  const { t } = useTranslation();
  const [time, setTime] = useState<string>('');
  const [guests, setGuests] = useState<number>(2);
  const [name, setName] = useState<string>(customerName ?? '');
  const [phone, setPhone] = useState<string>(customerPhone ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = !!customerName || !!customerPhone;
  const todayHours = restaurant.todayHours ?? null;

  const timeValid = TIME_RE.test(time) && (() => {
    if (!todayHours) return true;
    const m = timeToMin(time);
    return m >= timeToMin(todayHours.open) && m <= timeToMin(todayHours.close);
  })();

  const nameValid = name.trim().length >= 2;
  const phoneValid = PHONE_REGEX.test(phone.trim());

  const reset = () => {
    setTime('');
    setGuests(2);
    setName(customerName ?? '');
    setPhone(customerPhone ?? '');
    setSubmitting(false);
    setConfirmed(false);
    setError(null);
  };

  const handleClose = () => {
    onClose();
    setTimeout(reset, 300);
  };

  const submit = async () => {
    if (!timeValid) {
      setError(t('booking.timeInvalid', {
        defaultValue: todayHours
          ? `Время должно быть между ${todayHours.open} и ${todayHours.close}`
          : 'Введите корректное время',
      }));
      return;
    }
    if (!nameValid) {
      setError(t('booking.nameRequired', { defaultValue: 'Укажите имя' }));
      return;
    }
    if (!phoneValid) {
      setError(t('booking.phoneRequired', { defaultValue: 'Укажите корректный телефон' }));
      return;
    }
    setSubmitting(true);
    setError(null);
    // MVP: simulate request — show confirmation. Backend wiring is TODO.
    await new Promise((r) => setTimeout(r, 600));
    setSubmitting(false);
    setConfirmed(true);
  };

  if (confirmed) {
    return (
      <Sheet open={open} onClose={handleClose}>
        <SheetBody className="tbs-confirm">
          <div className="tbs-confirm-icon" aria-hidden="true">
            <CheckCircle size={56} strokeWidth={2.2} />
          </div>
          <h3 className="tbs-confirm-title">
            {t('booking.confirmTitle', { defaultValue: 'Заявка отправлена' })}
          </h3>
          <p className="tbs-confirm-sub">
            {t('booking.confirmSub', {
              defaultValue: `Стол на ${guests} ${guests === 1 ? 'человека' : 'чел.'} в ${time}. Мы перезвоним за 10 минут до брони.`,
              guests,
              time,
            })}
          </p>
          <Button variant="primary" size="lg" onClick={handleClose} className="tbs-confirm-btn">
            {t('common.close', { defaultValue: 'Закрыть' })}
          </Button>
        </SheetBody>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onClose={handleClose}>
      <SheetHeader>
        <SheetTitle>{t('booking.title', { defaultValue: 'Бронь столика' })}</SheetTitle>
      </SheetHeader>
      <SheetBody>
        {/* Time */}
        <div className="tbs-section">
          <label className="tbs-label" htmlFor="tbs-time">
            {t('booking.timeLabel', { defaultValue: 'На какое время?' })}
          </label>
          <input
            id="tbs-time"
            type="time"
            className="tbs-input tbs-input--time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            min={todayHours?.open}
            max={todayHours?.close}
          />
          {todayHours && (
            <p className="tbs-hint">
              {t('booking.openWindow', {
                defaultValue: `Сегодня открыто с ${todayHours.open} до ${todayHours.close}`,
                open: todayHours.open,
                close: todayHours.close,
              })}
            </p>
          )}
        </div>

        {/* Guest count */}
        <div className="tbs-section">
          <p className="tbs-label">
            {t('booking.guestsLabel', { defaultValue: 'Сколько гостей?' })}
          </p>
          <div className="tbs-guest-grid">
            {GUEST_OPTIONS.map((n) => (
              <button
                key={n}
                type="button"
                className={`tbs-guest-btn${guests === n ? ' tbs-guest-btn--active' : ''}`}
                onClick={() => setGuests(n)}
                aria-pressed={guests === n}
              >
                {n}{n === 6 ? '+' : ''}
              </button>
            ))}
          </div>
        </div>

        {/* Always-visible contact info — restaurant calls back to confirm */}
        <div className="tbs-section">
          <label className="tbs-label" htmlFor="tbs-name">
            {t('booking.nameLabel', { defaultValue: 'Имя' })}
          </label>
          <input
            id="tbs-name"
            type="text"
            autoComplete="name"
            placeholder={t('booking.namePlaceholder', { defaultValue: 'Как к вам обращаться' })}
            className="tbs-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <label className="tbs-label tbs-label--gap" htmlFor="tbs-phone">
            {t('booking.phoneLabel', { defaultValue: 'Телефон для подтверждения' })}
          </label>
          <input
            id="tbs-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+7 700 123 45 67"
            className="tbs-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <p className="tbs-hint">
            {t('booking.contactHint', {
              defaultValue: 'Перезвоним за 10 минут до брони чтобы подтвердить',
            })}
          </p>
        </div>

        {error && <p className="tbs-error" role="alert">{error}</p>}
      </SheetBody>

      <SheetActions>
        <Button
          variant="primary"
          size="lg"
          className="tbs-cta-primary"
          disabled={submitting || !timeValid || !nameValid || !phoneValid}
          aria-busy={submitting}
          onClick={submit}
        >
          {submitting
            ? t('common.loading')
            : t('booking.confirmCta', { defaultValue: 'Забронировать' })}
        </Button>
        {/* Optional perk — soft suggestion, never blocks */}
        {!isAuthenticated && onSignInRequired && (
          <button
            type="button"
            className="tbs-skip-link"
            onClick={onSignInRequired}
          >
            {t('booking.signInPerk', {
              defaultValue: 'Войти, чтобы копить бонусы за брони',
            })}
          </button>
        )}
      </SheetActions>
    </Sheet>
  );
}
